import {
  BodyContainer,
  HeaderContainer,
  PrimaryHeading,
  RightMainContainer,
  InputField,
  IconWrapper,
  MainButton,
  TextEditor,
  Loader,
  DeleteButton,
  SecondaryHeading,
  ListCard,
  ListIconWrapper,
} from "commonUI";
import { useEffect, useState } from "react";
import {
  BackArrow,
  CheckListIcon,
  ContentRequestIcon,
  SaveIcon,
  TaskIcon,
} from "allIcons";
import { getUserSavePermission, isEqual, parse, useUtilities } from "helpers";
import {
  useAddNewPhase,
  usePhases,
  useUpdatePhase,
  useDeletePhase,
} from "api/templateLibrary/phases";
import { useParams } from "react-router-dom";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

const ModuleIcons = {
  checklist: { icon: <CheckListIcon fill="white" /> , link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/checklists/` },
  content: { icon: <ContentRequestIcon />, link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/content-request-templates/` },
  task: { icon: <TaskIcon />, link: `/${ModulePrefixes.TEMPLATE_LIBRARY}/tasks/` },
};

export default function AddEditPhase() {
  const { id } = useParams();
  const { navigate, notification } = useUtilities();
  const [values, setValues] = useState({
    title: "",
    description: "",
    target_date_days: 10,
    status: { text: "Upcoming", color: "#FFD000" },
    assignments: [],
  });
  const { data: phases, isLoading: loadingPhases } = usePhases();
  const { mutate: addPhase, isLoading: isAdding } = useAddNewPhase(
    `/${ModulePrefixes.TEMPLATE_LIBRARY}/phases/`
  );
  const { mutate: updatePhase, isLoading: isUpdating } = useUpdatePhase();
  const { mutate: deletePhase, isLoading: isDeleting } = useDeletePhase(
    `/${ModulePrefixes.TEMPLATE_LIBRARY}/phases/`
  );
  const { add_new_templates, edit_templates, delete_templates } =
    useUserPermissions();

  useEffect(() => {
    if (isEqual(id, "add") || loadingPhases) return;

    const phase = phases.filter((ele) => isEqual(ele.id, id))[0];
    if (!phase) {
      navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/phases`);
      return;
    }
    setValues({
      title: phase?.title ?? "",
      description: phase?.description ?? "",
      target_date_days: phase?.target_date_days ?? 10,
      assignments: phase?.assignments ?? [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingPhases, phases, id]);

  const handleSaveChanges = () => {
    if (!values.title) {
      notification({
        type: "ERROR",
        message: t("common.input.error.titleRequired"),
      });
      return;
    }
    if (isEqual(id, "add")) {
      addPhase(values);
      return;
    }
    const phase = phases.filter((ele) => isEqual(ele.id, id))[0];
    updatePhase({ ...phase, status: parse(phase.status), ...values });
  };

  return (
    <>
      <RightMainContainer>
        <Loader loader="block" show={isAdding || isUpdating || isDeleting} />
        <HeaderContainer>
          <IconWrapper>
            <BackArrow onClick={() => navigate(`/${ModulePrefixes.TEMPLATE_LIBRARY}/phases`)} />
            <PrimaryHeading>
              {isEqual(id, "add") ? t("libraryTemplate.phases.addPhase") : values.title}
            </PrimaryHeading>
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={!isEqual(id, "add") && delete_templates}
              onDelete={() => deletePhase(id)}
              deleteTitle={t("libraryTemplate.delete.title")}
              deleteButtonText={t("libraryTemplate.delete.button")}
              deleteMessage={t("libraryTemplate.delete.message")}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_templates,
              editPermission: edit_templates,
            }) ? (
              <MainButton onClick={handleSaveChanges}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            label={t("common.input.title.label")}
            placeholder={t("common.input.title.placeholder")}
            value={values.title}
            setValue={(val) => setValues({ ...values, title: val })}
          />
          <TextEditor
            option={false}
            rows={5}
            label={t("common.input.description.label")}
            placeholder={t("common.input.description.placeholder")}
            value={values.description}
            setValue={(val) => setValues({ ...values, description: val })}
          />
          <InputField
            label={t("libraryTemplate.phases.input.phaseDurationDays")}
            type="number"
            value={values.target_date_days}
            setValue={(val) => setValues({ ...values, target_date_days: val })}
            className="mt-3 mb-3"
          />
          <div>
            <SecondaryHeading>{t("libraryTemplate.phases.input.associatedAssignments")}</SecondaryHeading>
            {values.assignments.map((assignment, index) => (
              <ListCard
                data={assignment.title}
                strictLink
                icon={
                  <ListIconWrapper>
                    {ModuleIcons[assignment.parent_type].icon}
                  </ListIconWrapper>
                }
                deleteIcon
                link={`${ModuleIcons[assignment.parent_type].link}${assignment.id}`}
              />
            ))}
          </div>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
